import { useMutation } from "@apollo/client";
import { useIonToast } from "@ionic/react";
import { useCallback } from "react";
import { useIntl } from "react-intl";

import useMutationWithValidation from "@hooks/useMutationWithValidation";
import {
  AnalyticsEventCreateDocument,
  AnalyticsEventCreateMutation,
  AnalyticsEventCreateMutationVariables,
  ArticleFragment,
  WebinarRegistrationCreateDocument,
  WebinarRegistrationCreateMutation,
  WebinarRegistrationCreateMutationVariables,
  WebinarRegistrationFragmentDoc
} from "@typing/Generated";

type ArticleType = Pick<
  ArticleFragment,
  "__typename" | "contentType" | "id" | "webinarCompleted" | "webinarRegistration"
>;

const useWebinarRegistration = (article: ArticleType, journeyId: string | null) => {
  const intl = useIntl();
  const [presentToast] = useIonToast();

  const [createWebinarRegistration] = useMutationWithValidation<
    WebinarRegistrationCreateMutation,
    WebinarRegistrationCreateMutationVariables
  >(WebinarRegistrationCreateDocument, "webinarRegistration", {
    update: (store, { data }) => {
      if (data?.webinarRegistrationCreate?.webinarRegistration?.id) {
        store.modify({
          fields: {
            webinarRegistration() {
              const newWebinarRegistrationRef = store.writeFragment({
                data: data.webinarRegistrationCreate?.webinarRegistration,
                fragment: WebinarRegistrationFragmentDoc,
                fragmentName: "WebinarRegistration"
              });
              if (newWebinarRegistrationRef) {
                return newWebinarRegistrationRef;
              }
              return null;
            }
          },
          id: store.identify({ ...article })
        });
      }
    }
  });

  const [createAnalyticsEvent] = useMutation<AnalyticsEventCreateMutation, AnalyticsEventCreateMutationVariables>(
    AnalyticsEventCreateDocument
  );

  const handleZoomMeetingRegistration = useCallback(
    (addAnalyticsEvent: boolean) => {
      createWebinarRegistration({
        journeyId,
        webinarId: article.id
      }).then(response => {
        if (!response?.webinarRegistrationCreate || response.webinarRegistrationCreate.errors.length > 0) {
          presentToast({
            buttons: [
              {
                handler: () => {
                  window.open("mailto: support@withgrayce.com", "_self");
                },
                text: intl.formatMessage({ id: "models.webinar.contactSupport" })
              }
            ],
            color: "danger",
            cssClass: "toast",
            duration: 8000,
            layout: "stacked",
            message: intl.formatMessage({ id: "models.webinar.registrationFailed" }),
            position: "top"
          });
        } else {
          presentToast({
            color: "success",
            cssClass: "toast",
            duration: 8000,
            message: intl.formatMessage({ id: "models.webinar.registrationSuccessful" }),
            position: "top"
          });

          if (addAnalyticsEvent) {
            createAnalyticsEvent({
              variables: {
                eventData: {
                  page: location.pathname,
                  type: "registrationCreatedToAttendWebinar",
                  webinarId: article.id
                }
              }
            });
          }
        }
      });
    },
    [article.id, createAnalyticsEvent, createWebinarRegistration, intl, journeyId, presentToast]
  );

  return handleZoomMeetingRegistration;
};

export default useWebinarRegistration;
